import { getLocks, type LockRow } from '../endpoints/getLocks';

// `GET /locks` defaults to 50 rows and enforces no maximum on `limit`, so one
// large page covers every installation seen so far (largest reported: ~105
// locks). The loop below is the safety net, not the normal path.
const PAGE_SIZE = 2000;

/**
 * Every lock the current user may list, following pagination to exhaustion.
 *
 * The node-definition editor's lock pickers filter and sort client-side, so a
 * lock absent from the response is a lock the user cannot pick at all — and the
 * flow editor's picker, which narrows to the flow's own zone, reports "no locks
 * left" rather than a short list when the truncated page happens to hold none of
 * that zone's locks. Both need the complete set.
 */
export const getAllLocks = async (): Promise<readonly LockRow[]> => {
  const rows: LockRow[] = [];
  const seen = new Set<number>();
  let fetched = 0;
  let total = 0;

  do {
    const page = await getLocks({ limit: PAGE_SIZE, offset: fetched });

    // An empty page means the server has no more to give, whatever its total
    // claims — stop rather than spin. Every other path advances `fetched`, so
    // the loop always terminates.
    if (page.locks.length === 0) {
      break;
    }

    fetched += page.locks.length;
    total = page.pagination.total_records;

    // Offset paging over a table that can gain rows mid-sequence may repeat a
    // row across pages; `id` keeps the result a set, so no duplicate reaches a
    // consumer that keys a list on it.
    for (const lock of page.locks) {
      if (!seen.has(lock.id)) {
        seen.add(lock.id);
        rows.push(lock);
      }
    }
  } while (fetched < total);

  return rows;
};
